import { createClipRootProxy } from '@src/components/content-components/hooks/clipTranscriptProxies';
import { TranscriptSpeakersAdapter } from '@src/components/content-components/TranscriptSpeakersTab/TranscriptSpeakersAdapter';
import { TranslationReviewWorkspace } from '@src/components/transcript/TranslationReviewWorkspace';
import { resolveReviewSavedCaptionStyle } from '@src/editor/services/reviewCaptionTrack';
import {
  StoreContext,
  useAnalytics,
  useDatoClient,
  useStore,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import type { CreatorStudioDubGeneration } from '@src/components/aiVoices/useCreatorStudioDubGeneration';
import type { ReviewMusicSeparation } from '@src/components/transcript/ReviewPlayerAudioPickers';
import type { ReviewMode } from '@src/components/transcript/TranslationReviewHeader';
import type { Language } from '@src/types.ts/language';

interface Props {
  /** The translation under review — the transcript's selected language. */
  language: string;
  /** Which job the review opens on: the pencil's or the microphone's. */
  mode: ReviewMode;
  supportedLanguages: Language[];
  /** This screen's dub controls, so the review's Voice and Music follow them. */
  dubGeneration: CreatorStudioDubGeneration;
  /** Dubbing is switched on for this workspace (flag + activation). */
  dubEntryEnabled: boolean;
  onClose: () => void;
}

/**
 * PLAT-11877 — Translation Review and Dubbing Review over Creator Studio, the
 * same workspace the Transcript & Speakers tab and the clip preview open, so
 * a reviewer no longer has to leave the editor to find it.
 *
 * The workspace must not run against Creator Studio's live timeline editor:
 * there, every edit is a command with undo and autosave, and the review's
 * editable original column and review-only caption track would land on the
 * real timeline. So this host follows the Transcript & Speakers story-entry
 * pattern: a `TranscriptSpeakersAdapter` with its own scoped editor and
 * `VideoSourceStore`, served to the workspace through `createClipRootProxy`,
 * playing the story's original video. Translation edits reach the editor's
 * transcript the same way they reach the tab's — through the shared
 * `TranslationStore`, which saves itself.
 *
 * Kept out of `LeftSidePanel` so that file stays a host: everything the review
 * needs that the panel does not already have lives here.
 */
export const CreatorStudioTranslationReview: React.FC<Props> = observer(
  ({
    language,
    mode,
    supportedLanguages,
    dubGeneration,
    dubEntryEnabled,
    onClose,
  }) => {
    const videoCreator = useVideoCreatorStore();
    const realRootStore = useStore();
    const datoClientStore = useDatoClient();
    const transcription = useTranscription();
    const analyticsStore = useAnalytics();
    const userIdentity = useUserIdentity();
    const flags = useFlagsCombination(userIdentity.currentRole);
    const storyId = videoCreator.storyId ?? null;

    // A scoped editor of its own, as the Transcript & Speakers tab builds:
    // the review's player and caption track never touch the timeline.
    const videoSourceStore = useMemo(
      () => new VideoSourceStore(analyticsStore),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [analyticsStore, storyId],
    );
    const adapter = useMemo(
      () =>
        new TranscriptSpeakersAdapter({
          realVideoCreator: videoCreator,
          videoSourceStore,
          transcriptRepository: datoClientStore.transcriptRepository,
          transcriptionStore: transcription,
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [videoCreator, videoSourceStore, datoClientStore, transcription, storyId],
    );
    const scopedEditor = adapter.getScopedEditor();
    const scopedRootProxy = useMemo(
      () => createClipRootProxy(realRootStore, scopedEditor, videoSourceStore),
      [realRootStore, scopedEditor, videoSourceStore],
    );

    const initializedForRef = useRef<TranscriptSpeakersAdapter | null>(null);
    useEffect(() => {
      if (!transcription.isTranscriptionReady) return;
      if (initializedForRef.current === adapter) return;
      initializedForRef.current = adapter;
      void adapter.initializeEditor();
    }, [adapter, transcription.isTranscriptionReady]);

    // The review has a player of its own; the editor's canvas stands down
    // while it is up (the workspace's pause broadcast is heard only by the
    // Transcript & Speakers tab's player).
    useEffect(() => {
      videoCreator.renderer?.pause();
    }, [videoCreator]);

    // The showcase's caption styles for the review's Captions picker, and the
    // story's own style when it has one — the same reads the T&S tab makes.
    const reviewCaptionAspectRatio = videoCreator.getCurrentAspectRatio();
    const showcaseKaraokeTemplates =
      videoCreator.organization?.karaokeTextBranding;
    const showcaseLastUsedTemplates =
      videoCreator.organization?.lastUsedTemplates;
    const reviewCaptionTemplates = useMemo(
      () =>
        videoCreator.textBrandingService.getCustomKaraokeTemplateConfigs(
          reviewCaptionAspectRatio,
        ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        videoCreator,
        reviewCaptionAspectRatio,
        showcaseKaraokeTemplates,
        showcaseLastUsedTemplates,
      ],
    );
    const reviewCaptionFallbackTemplate = reviewCaptionTemplates[0];
    const resolveCaptionTemplateName = useCallback(
      (templateId: string) =>
        videoCreator.textBrandingService.getKaraokeTemplateTitle(templateId),
      [videoCreator],
    );
    const isPresetCaptionTemplate = useCallback(
      (templateId: string) =>
        videoCreator.textBrandingService.isPresetTemplate(templateId),
      [videoCreator],
    );
    const storyCaptionStyle = realRootStore.karaokeStore.hasKaraokeLines
      ? realRootStore.karaokeStore.defaultKaraokeConfig
      : null;
    const storySavedCaptionStyle = resolveReviewSavedCaptionStyle(
      storyCaptionStyle,
      !!reviewCaptionFallbackTemplate,
    );
    // Stable identity: the review's caption effect depends on it, and the
    // refresh re-renders this observer (see the T&S tab's note).
    const refreshReviewKaraokeState = useCallback(
      () => scopedEditor.refreshKaraokeState(),
      [scopedEditor],
    );

    const { musicController, composition } = dubGeneration;
    const storyDurationSec =
      videoCreator.story?.originalVideo?.video?.duration ?? 0;
    const separationTooLong =
      storyDurationSec > flags.stemSeparationMaxMinutes * 60;
    const reviewMusicSeparation: ReviewMusicSeparation = useMemo(
      () => ({
        separating: musicController.status === 'running',
        startedAt: musicController.runStartedAt,
        durationSec: storyDurationSec,
        hasStems: musicController.hasStems,
        noMusic: musicController.hasStems && musicController.noMusic,
        blockedReason: separationTooLong
          ? `Music separation is available for tracks ${flags.stemSeparationMaxMinutes} minutes or less.`
          : musicController.status === 'loading'
            ? 'Checking this story’s audio…'
            : null,
        onSeparate: musicController.separate,
        onCancel: musicController.cancel,
      }),
      [
        musicController.status,
        musicController.runStartedAt,
        musicController.hasStems,
        musicController.noMusic,
        musicController.separate,
        musicController.cancel,
        storyDurationSec,
        separationTooLong,
        flags.stemSeparationMaxMinutes,
      ],
    );

    return (
      <StoreContext.Provider value={scopedRootProxy}>
        <TranslationReviewWorkspace
          language={language}
          videoCreator={adapter}
          supportedLanguages={supportedLanguages}
          zoomLevel={0}
          enableDubMonitor={
            dubEntryEnabled && composition.dubbedLanguages.includes(language)
          }
          // The first dub, offered from inside Dubbing Review when the
          // reviewed language has none — this screen's own run, over the
          // timeline's lines, under the same length cap.
          onRequestDub={
            dubEntryEnabled &&
            !dubGeneration.dubUnavailableNote &&
            dubGeneration.onRequestFirstDub
              ? () => dubGeneration.onRequestFirstDub?.()
              : undefined
          }
          // A re-dub inside the review changed the story's dub: rebuild this
          // screen's composed audio from the new manifest.
          onDubUpdated={() => {
            composition.refreshDubbedLanguages?.();
            composition.reapplyComposition?.();
          }}
          initialAudioMode={composition.audioMode}
          initialMusicMode={musicController.musicMode}
          dubMusicBedUrl={musicController.bedUrl}
          dubVoiceStemUrl={
            musicController.voiceUrlMp3 ?? musicController.voiceUrlWav
          }
          musicSeparation={reviewMusicSeparation}
          initialReviewMode={mode}
          layout="page"
          storyCaptions={{
            isReady: scopedEditor.isReady,
            editor: scopedEditor.editor,
            karaokeLines: scopedEditor.karaokeLines,
            refreshKaraokeState: refreshReviewKaraokeState,
            savedStyle: storySavedCaptionStyle,
          }}
          captionFallbackTemplate={reviewCaptionFallbackTemplate}
          captionTemplates={reviewCaptionTemplates}
          resolveCaptionTemplateName={resolveCaptionTemplateName}
          isPresetCaptionTemplate={isPresetCaptionTemplate}
          onClose={onClose}
        />
      </StoreContext.Provider>
    );
  },
);
